// Public legal pages — Privacy, Terms, Refund, Cookies
import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Logo } from "@/components/Logo";
import { api } from "@/lib/api";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useSitePageVisibility } from "@/context/SitePageVisibilityContext";
import { HiddenPagePlaceholder } from "@/components/PageGate";

// Map URL slug → page-visibility key (backend keys use underscores).
const SLUG_TO_KEY = {
  "terms-of-service": "legal_terms",
  "terms-conditions": "legal_terms",
  "privacy-policy": "legal_privacy",
  "refund-policy": "legal_refund",
  "cookies-policy": "legal_cookies",
};

// Format YYYY-MM-DD → DD-MM-YYYY per spec ("Last Updated: 07-07-2026")
function formatDate(iso) {
  if (!iso || typeof iso !== "string") return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return `${m[3]}-${m[2]}-${m[1]}`;
}

const KNOWN_SLUGS = ["privacy-policy", "terms-conditions", "refund-policy", "cookies-policy"];

export default function LegalPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { pages, loading: visLoading } = useSitePageVisibility();
  const visKey = SLUG_TO_KEY[slug];
  // Direct-URL rule: only hide when page_active is explicitly false.
  // show_in_footer=false alone leaves the URL usable (unlisted mode).
  const entry = visKey ? pages[visKey] : undefined;
  const isHidden = !visLoading && entry !== undefined && (
    typeof entry === "boolean" ? entry === false : entry.page_active === false
  );
  usePageTitle(page?.title || "Legal");

  useEffect(() => {
    if (isHidden) return undefined;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/public/legal-pages/${slug}`);
        if (alive) setPage(data.data);
      } catch {
        if (alive) setNotFound(true);
      }
    })();
    return () => { alive = false; };
  }, [slug, isHidden]);

  if (isHidden) {
    return <HiddenPagePlaceholder />;
  }

  if (!slug || !KNOWN_SLUGS.includes(slug)) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid={`legal-page-${slug}`}>
      <header className="sticky top-0 z-40 border-b border-slate-200/70 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-4xl items-center justify-between px-4 md:h-20 md:px-6">
          <Link to="/"><Logo /></Link>
          <Link to="/" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-[#101876]">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-10 md:px-6 md:py-14">
        {notFound ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center">
            <FileText className="mx-auto h-8 w-8 text-slate-400" />
            <div className="mt-3 text-sm font-bold text-slate-900">Page not published yet</div>
            <p className="mt-1 text-xs text-slate-500">Please try again later.</p>
          </div>
        ) : !page ? (
          <div className="h-64 animate-pulse rounded-2xl bg-white" />
        ) : (
          <article className="rounded-2xl border border-slate-200 bg-white p-6 md:p-10">
            <div className="mb-6 border-b border-slate-100 pb-6">
              <h1 className="text-3xl font-black text-slate-900 md:text-4xl" data-testid="legal-title">{page.title}</h1>
              <div className="mt-2 text-xs font-semibold uppercase tracking-widest text-slate-500" data-testid="legal-last-updated">
                Last Updated: {formatDate(page.last_updated_date)}
              </div>
            </div>
            <div className="space-y-3 text-slate-700" data-testid="legal-body">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: () => null,
                  h2: ({node, ...p}) => <h2 className="mt-8 text-xl font-black text-slate-900 md:text-2xl" {...p} />,
                  h3: ({node, ...p}) => <h3 className="mt-5 text-base font-bold text-slate-900" {...p} />,
                  p: ({node, ...p}) => <p className="text-sm leading-relaxed text-slate-700" {...p} />,
                  ul: ({node, ...p}) => <ul className="ml-5 list-disc space-y-1 text-sm text-slate-700" {...p} />,
                  ol: ({node, ...p}) => <ol className="ml-5 list-decimal space-y-1 text-sm text-slate-700" {...p} />,
                  li: ({node, ...p}) => <li className="leading-relaxed" {...p} />,
                  a:  ({node, ...p}) => <a className="text-[#101876] hover:underline" {...p} />,
                  strong: ({node, ...p}) => <strong className="font-semibold text-slate-900" {...p} />,
                  table: ({node, ...p}) => <div className="my-4 overflow-x-auto"><table className="w-full border-collapse text-sm" {...p} /></div>,
                  th: ({node, ...p}) => <th className="border border-slate-200 bg-slate-50 px-3 py-2 text-left font-semibold text-slate-800" {...p} />,
                  td: ({node, ...p}) => <td className="border border-slate-200 px-3 py-2 align-top" {...p} />,
                  code: ({node, ...p}) => <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[12px] text-slate-800" {...p} />,
                }}
              >
                {page.content_md}
              </ReactMarkdown>
            </div>
          </article>
        )}
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-4xl px-4 py-6 text-center text-xs text-slate-500 md:px-6">
          © {new Date().getFullYear()} Steward Healthcare India Pvt. Ltd. · All rights reserved.
        </div>
      </footer>
    </div>
  );
}
